import { useMemo } from 'react';
import { useMutation, UseMutationResult } from 'react-query';
import { useTranslation } from 'react-i18next';
import { flatten, isArray, isEmpty, partition, sortBy, startCase, sumBy, toLower } from 'lodash';
import moment from 'moment';

import { useAppLocale } from '@pimm/common';
import { HiveSiteSimpleDto } from '@pimm/services/lib/sms-tenants';
import {
  HiveGetSummaryReportsResponseDto,
  ReviewInfoDto,
  ReportScheduleTypeEnum,
  StringTranslation,
  HiveDaySummaryDto,
} from '@pimm/services/lib/store-inspections/types';
// import { ExportSheet } from '@/core/hooks/export-to.hook';

// Local fallback type
export type WeekFocus = { weekNumber: number; startDate: Date; endDate: Date };
interface KeyValue<T = number> {
  key: string;
  value: T;
}

export interface StoreReportEntry {
  id?: string;
  date: KeyValue<string | undefined>;
  scheduleType: ReportScheduleTypeEnum;
  lateItems: KeyValue;
  notStarted: KeyValue;
  partiallyCompleted: KeyValue;
  totalForms: KeyValue;
  completed: KeyValue;
  completionPercentage: KeyValue<string>;
  dmReview: KeyValue<ReviewInfoDto | undefined>;
  eodReview: KeyValue<ReviewInfoDto | undefined>;
  entries?: StoreReportEntry[];
}

export interface StoreReport {
  date?: string;
  name?: string;
  reportId: string;
  reportType?: string;
  scheduleType: ReportScheduleTypeEnum;
  translations?: StringTranslation[];
  isReviewed?: boolean;
  entries: StoreReportEntry[];
}

type StoreReportsTableDataReturn = {
  data: Record<string, StoreReport[]>;
  download: UseMutationResult<unknown, Error, string, unknown>;
};

export const useCombineStoreReports = (
  _site?: Partial<HiveSiteSimpleDto>,
  _week?: WeekFocus,
  _queries?: HiveGetSummaryReportsResponseDto | HiveGetSummaryReportsResponseDto[],
): StoreReportsTableDataReturn => {
  const { t } = useTranslation();
  const { translate } = useAppLocale();
  // const { xlsxExport } = useExportTo();

  const exportExcel = useMutation<unknown, Error, string>({
    mutationFn: (_siteId: string) => {
      const reports = tableData[_siteId];
      // const sheets = reports.reduce((_sheets: ExportSheet[], _report) => {
      //   if (isEmpty(_report.entries?.[0])) return _sheets;

      //   const composeColumns = (_scheduleType: ReportScheduleTypeEnum, _entry: StoreReportEntry, indent?: number) => {
      //     const reviewers = [_entry.eodReview, _entry.dmReview];
      //     const totalForms = _entry.totalForms.value;

      //     const columns = {
      //       [_entry.date.key]: `${' '.repeat(indent ?? 0) + moment(_entry.date.value).format('dddd (MM/DD/YY)')}`,
      //       [_entry.totalForms.key]: !totalForms ? '' : totalForms,
      //       [_entry.completionPercentage.key]: !totalForms ? '' : _entry.completionPercentage.value,
      //       [_entry.completed.key]: !totalForms ? '' : _entry.completed.value,
      //       [_entry.partiallyCompleted.key]: !totalForms ? '' : _entry.partiallyCompleted.value,
      //       [_entry.notStarted.key]: !totalForms ? '' : _entry.notStarted.value,
      //       [_entry.lateItems.key]: !totalForms ? '' : _entry.lateItems.value,
      //       ...reviewers.reduce((_obj: Record<string, string>, { key, value: reviewer }) => {
      //         let name: string | undefined;
      //         let timestamp: string | undefined;
      //         if (reviewer) {
      //           const { reviewerUserName, reviewTimestampUtc } = reviewer;
      //           const [username, firstName, lastName] = reviewerUserName?.split('$') ?? [];
      //           name = startCase(toLower([firstName, lastName].filter(Boolean).join(' ')));
      //           timestamp = reviewTimestampUtc ? moment(reviewTimestampUtc).local().format('MM/DD/YYYY hh:mm A') : undefined;
      //         }
      //         return { ..._obj, [t(key)]: [name, timestamp].filter(Boolean).join('\n') };
      //       }, {}),
      //     };

      //     return columns;
      //   };

      //   const rows = _report.entries.reduce((_columns: Record<string, any>[], _entry) => {
      //     let columns = [composeColumns(_report.scheduleType, _entry)];

      //     if (_report.scheduleType === ReportScheduleTypeEnum.Weekly) {
      //       const date = moment(_entry.date.value);
      //       const name = `Wk ${_week?.weekNumber} (${date.format('MMM DD')} - ${date.add(6, 'days').format('MMM DD')})`;
      //       columns[0][_entry.date.key] = name;
      //     }

      //     _entry.entries?.forEach(_subEntry => {
      //       columns.push(composeColumns(_report.scheduleType, _subEntry, 2));
      //     });

      //     return [..._columns, ...columns];
      //   }, []);

      //   const sheet: ExportSheet = {
      //     sheet: translate(_report.name, _report.translations),
      //     data: rows.map(_row => {
      //       // Translate all column keys
      //       return Object.keys(_row).reduce((_columns: Record<string, any>, _key) => {
      //         return { ..._columns, [t(_key)]: _row[_key] };
      //       }, {});
      //     }, []),
      //   };

      //   return [..._sheets, sheet];
      // }, []);

      // xlsxExport(`${_site?.name} - Reports Summary`, sheets);

      return new Promise(resolve => setTimeout(resolve, 2000));
    },
  });

  const tableData = useMemo(() => {
    if (!_queries) return {};

    const queries = isArray(_queries) ? _queries : [_queries];
    const storeSummaries: StoreReportsTableDataReturn['data'] = {};

    const dataFields: { id: keyof StoreReportEntry; field?: keyof HiveDaySummaryDto; label: string }[] = [
      { id: 'date', label: 'date', field: 'date' },
      { id: 'totalForms', label: 'total_forms' },
      { id: 'completed', label: 'completed' },
      { id: 'completionPercentage', label: 'completion_percentage' },
      { id: 'lateItems', label: 'late', field: 'formsWithLateItems' },
      { id: 'notStarted', label: 'not_started', field: 'formsNotStarted' },
      { id: 'partiallyCompleted', label: 'partially_completed', field: 'formsPartiallyCompleted' },
      { id: 'eodReview', label: 'eod_review', field: 'eodReview' },
      { id: 'dmReview', label: 'weekly_review' },
    ];

    for (const query of queries) {
      const eodManagers = query.eodManagers;
      const summaryReportsByReportType = query.summaryReportsByReportType ?? [];
      const reports: StoreReport[] = [];

      for (const { reportScheduleType = ReportScheduleTypeEnum.None, ..._report } of summaryReportsByReportType) {
        if (!_report.summaryReports?.length) continue;

        const isDaily = reportScheduleType === ReportScheduleTypeEnum.Daily;
        let entries: StoreReportEntry[] = [];

        // -- Flatten all report entry by dates
        for (const { daySummaries = [], dmReview, storeReportId } of _report.summaryReports) {
          const days = sortBy(daySummaries, ['date']);

          for (const day of days) {
            const completed = day.formsCompleted ?? 0;
            const totalForms = day.totalForms ?? 0;
            const completion = completed > 0 || totalForms > 0 ? (completed / totalForms) * 100 : 0;

            const entry = {
              id: storeReportId,
              scheduleType: reportScheduleType,
              ...dataFields.reduce((_keyValues: Partial<StoreReportEntry>, _) => {
                const value = _.field ? day[_.field] : undefined;
                return { ..._keyValues, [_.id]: { key: `store_inspections:${_.label}`, value } };
              }, {}),
            } as StoreReportEntry;

            entry.completed.value = completed;
            entry.completionPercentage.value = `${completion.toFixed(0)}%`;
            entry.totalForms.value = totalForms;

            if (dmReview) {
              entry.dmReview.value = {
                reviewerUserId: dmReview.userId,
                reviewerUserName: dmReview.userName,
                reviewTimestampUtc: dmReview.reviewTimestampUtc,
              };
            }

            // use assign leader if eodReview is not available
            if (!entry.eodReview?.value) {
              const eodLeader = eodManagers?.find(_leader => _leader.date === day.date);
              if (eodLeader) {
                entry.eodReview.value = {
                  reviewerUserId: eodLeader?.userId,
                  reviewerUserName: eodLeader.name,
                  reviewerTitle: eodLeader.title,
                };
              }
            }

            if (!isDaily && !entry.totalForms.value) continue;

            entries.push(entry);
          }
        }

        // -- Create a new parent entry for weekly report
        if (!isDaily) {
          const entry = {
            id: entries[0]?.id,
            scheduleType: reportScheduleType,
            ...dataFields.reduce((_keyValues: Partial<StoreReportEntry>, _) => {
              return { ..._keyValues, [_.id]: { key: `store_inspections:${_.label}` } };
            }, {}),
          } as StoreReportEntry;

          const sumCompleted = sumBy(entries, 'completed.value');
          const sumTotals = sumBy(entries, 'totalForms.value');
          const sumCompletion = sumCompleted > 0 && sumTotals > 0 ? (sumCompleted / sumTotals) * 100 : 0;

          entry.date.value = entries[0]?.date.value;
          entry.dmReview.value = entries[0]?.dmReview.value;

          entry.completed.value = sumCompleted;
          entry.completionPercentage.value = `${sumCompletion.toFixed(0)}%`;
          entry.totalForms.value = sumTotals;
          entry.notStarted.value = sumBy(entries, 'notStarted.value');
          entry.partiallyCompleted.value = sumBy(entries, 'partiallyCompleted.value');
          entry.lateItems.value = sumBy(entries, 'lateItems.value');

          // override entries using nested data
          entries = [{ ...entry, entries: entries }];
        }

        const report: StoreReport = {
          name: _report.reportName,
          reportId: _report.reportId!,
          reportType: _report.reportTypeName,
          scheduleType: reportScheduleType,
          translations: _report.translations,
          isReviewed: !entries.some(_ => !_.dmReview.value && !!_.totalForms.value),
          entries: entries,
        };

        // Skip empty report
        if (!report.entries.length) continue;

        reports.push(report);
      }

      if (query.siteId) {
        // sorting should be based on reportScheduleType (1. Weekly, 2. Daily)
        const [dailyReports, otherReports] = partition(reports, _ => _.scheduleType === ReportScheduleTypeEnum.Daily);
        storeSummaries[query.siteId] = flatten([otherReports, dailyReports]);
      }
    }

    return storeSummaries;
  }, [_queries]);

  return { data: tableData, download: exportExcel };
};
